import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(optionHeaders || {}),
  };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
    });
    const json = await res.json().catch(() => ({}));
    if (res.status === 409 && !json.status_code && !json.statusCode) {
      json.status_code = '10001';
      json.message =
        json.message ||
        'Cannot replace requirements while this mission is active and users have it assigned. Deactivate the mission first, then save again.';
    }
    return json;
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

const authFetchWithRetry = async (path, options = {}) => {
  let res = await authFetch(path, options);
  if (isMissionsAuthError(res)) {
    await new Promise((resolve) => setTimeout(resolve, 800));
    res = await authFetch(path, options);
  }
  return res;
};

export const isMissionsApiSuccess = (res) =>
  String(res?.statusCode || res?.status_code || '') === '10000';

export const getMissionsPayload = (res) => res?.data ?? null;

export const isMissionsAuthError = (res) => {
  const code = String(res?.statusCode || res?.status_code || '');
  const message = String(res?.message || '').toLowerCase();

  if (code === '10003') return true;

  return (
    code === '10001' &&
    (message.includes('token') ||
      message.includes('jwt') ||
      message.includes('credential') ||
      message.includes('authenticated'))
  );
};

export const isRequirementsReplaceBlocked = (res) => {
  const message = String(res?.message || '').toLowerCase();
  return message.includes('cannot replace requirements');
};

export const getMissionsErrorMessage = (res, fallback) => {
  if (isMissionsAuthError(res)) {
    return 'Session token is invalid or expired. Log out, log in again, then reopen this page.';
  }

  const message = String(res?.message || '');
  if (isRequirementsReplaceBlocked(res)) {
    return (
      message ||
      'Cannot replace requirements while this mission is active and users have it assigned. Deactivate the mission first, then save again.'
    );
  }
  if (message.toLowerCase().includes('invalid mission id')) {
    return 'Mission API route mismatch on backend. Register /dashboard/summary and /participants before /:id.';
  }

  return message || fallback;
};

/** GET /admin/missions */
export const listMissions = async () =>
  authFetchWithRetry('/admin/missions', { method: 'GET' });

/** GET /admin/missions/actions */
export const listMissionActions = async () =>
  authFetchWithRetry('/admin/missions/actions', { method: 'GET' });

/** GET /admin/missions/:id */
export const getMission = async (id) =>
  authFetchWithRetry(`/admin/missions/${id}`, { method: 'GET' });

/** POST /admin/missions */
export const createMission = async (body) =>
  authFetch('/admin/missions', { method: 'POST', body: JSON.stringify(body) });

/** PUT /admin/missions/:id */
export const updateMission = async (id, body) =>
  authFetch(`/admin/missions/${id}`, { method: 'PUT', body: JSON.stringify(body) });

/** PATCH /admin/missions/:id/active */
export const setMissionActive = async (id, isActive) =>
  authFetch(`/admin/missions/${id}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  });

/** GET /admin/missions/dashboard/summary */
export const getMissionDashboardSummary = async () =>
  authFetchWithRetry('/admin/missions/dashboard/summary', { method: 'GET' });

const parseMissionId = (value) => {
  if (value == null || value === '') return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/** GET /admin/missions/participants */
export const listParticipants = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.limit != null) {
    const limit = Math.min(100, Math.max(1, Number(filters.limit) || 20));
    params.set('limit', String(limit));
  }
  if (filters.offset != null) params.set('offset', String(filters.offset));

  const missionId = parseMissionId(filters.mission_id);
  if (missionId != null) params.set('mission_id', String(missionId));

  if (filters.status) params.set('status', filters.status);
  if (filters.subject_type) params.set('subject_type', filters.subject_type);
  if (filters.search) params.set('search', filters.search);

  const qs = params.toString();
  return authFetchWithRetry(`/admin/missions/participants${qs ? `?${qs}` : ''}`, { method: 'GET' });
};

/** GET /admin/missions/participants/:subjectType/:subjectId */
export const getParticipantDetail = async (subjectType, subjectId) =>
  authFetchWithRetry(`/admin/missions/participants/${subjectType}/${encodeURIComponent(subjectId)}`, {
    method: 'GET',
  });
